/* GENERATED from src/js/ndog-mounted-tabs.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-mounted-tabs.js  (v2)
  Self-contained web component for the NDOG Mounted Tabs feature.
  Groups sibling sections by the data-ndog-tab key that each section renders
  (via the ndog-tab-marker snippet) — no separate marker sections needed.
  Grouping is GLOBAL per key: every distinct key = one tab, regardless of
  position; tab order = first appearance; label = first section's data-tab-label
  (falls back to a humanized key). Sections without a key stay always visible.
  Builds the tab buttons and toggles the active group with a directional
  slide+fade. Supports mobile swipe, URL deep-link (#tab-key), keyboard
  navigation and the Shopify theme editor. No dependencies.
*/
if (!customElements.get('ndog-mounted-tabs')) {
  customElements.define(
    'ndog-mounted-tabs',
    class NdogMountedTabs extends HTMLElement {
      // Wire everything up; bail out cleanly if there are no tab groups.
      connectedCallback() {
        this.list = this.querySelector('.ndog-tabs__list');
        // Optional CTA link rendered by Liquid — renderTabs() moves it to the end of the tab row.
        this.ctaLink = this.querySelector('[data-tabs-cta]');
        this.sectionWrapper = this.closest('.shopify-section');
        this.container = this.sectionWrapper ? this.sectionWrapper.parentElement : null;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.activeIndex = null;
        // Sticky on/off. The actual offset (px) is read live from CSS (getStickyOffset) so
        // the mobile/desktop breakpoints defined in the section CSS stay the single source of truth.
        this.sticky = this.dataset.sticky != null;

        this.buildGroups();
        if (this.groups.length === 0) return;

        this.container.classList.add('ndog-tabs-scope');
        this.renderTabs();
        this.bindEvents();
        this.activate(this.getInitialIndex(), { animate: false });
        this.setupStickyRelease();

        // Button widths can shift once the heading font loads — re-tag the first
        // row and re-align the pill.
        if (document.fonts) {
          document.fonts.ready.then(() => {
            this.markFirstRow();
            this.movePill(false);
          });
        }
      }

      // Remove listeners and restore the page to its untouched state.
      disconnectedCallback() {
        if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        if (this.onSelect) document.removeEventListener('shopify:section:select', this.onSelect);
        if (this.onSectionChange) {
          document.removeEventListener('shopify:section:load', this.onSectionChange);
          document.removeEventListener('shopify:section:unload', this.onSectionChange);
        }
        if (this.onStickyScroll) {
          window.removeEventListener('scroll', this.onStickyScroll);
          window.removeEventListener('resize', this.onStickyScroll);
        }
        if (this.sectionWrapper) this.sectionWrapper.style.transform = '';
        if (this.container) this.container.classList.remove('ndog-tabs-scope');
        this.resetSections();
      }

      // Walk the sibling section wrappers once and group them GLOBALLY by key.
      // Every distinct data-ndog-tab key = one tab (order = first appearance;
      // label = first tagged section for that key). A KEYLESS section that sits
      // BETWEEN the first and last tagged section is absorbed into the current
      // (preceding) tab, so a stray untagged section inside the tab area doesn't
      // break visibility. Keyless sections before/after the tab area stay
      // always-visible (normal flow).
      buildGroups() {
        this.groups = [];
        if (!this.container) return;

        const children = Array.from(this.container.children);
        const keyOf = (el) => {
          const m = el.querySelector('[data-ndog-tab]');
          return m ? (m.getAttribute('data-ndog-tab') || '').trim() : '';
        };
        const keys = children.map(keyOf);

        let firstKeyed = -1;
        let lastKeyed = -1;
        keys.forEach((k, i) => {
          if (k) {
            if (firstKeyed === -1) firstKeyed = i;
            lastKeyed = i;
          }
        });
        if (firstKeyed === -1) return; // no tagged sections → no tabs

        const byKey = new Map();
        let currentKey = null;

        children.forEach((el, i) => {
          let key = keys[i];
          if (!key) {
            // Keyless: only absorb into the current tab while inside the tab span.
            if (i > firstKeyed && i < lastKeyed && currentKey) key = currentKey;
            else return; // outside the tab area → stays always visible
          } else {
            currentKey = key;
          }

          let group = byKey.get(key);
          if (!group) {
            const marker = el.querySelector('[data-ndog-tab]');
            group = {
              key,
              label: marker ? (marker.getAttribute('data-tab-label') || '').trim() : '',
              sections: [],
            };
            byKey.set(key, group);
            this.groups.push(group);
          }
          group.sections.push(el);
        });

        this.ensureUniqueKeys();
      }

      // Safety net: guarantee every group has a non-empty, unique key.
      ensureUniqueKeys() {
        const seen = new Set();
        this.groups.forEach((g, i) => {
          let key = g.key || `tab-${i + 1}`;
          while (seen.has(key)) key = `${key}-${i}`;
          seen.add(key);
          g.key = key;
        });
      }

      // Turn a handleized key into a readable fallback label.
      humanize(key) {
        if (!key) return '';
        const text = key.replace(/[-_]+/g, ' ').trim();
        return text.charAt(0).toUpperCase() + text.slice(1);
      }

      // Build one button per group inside the tablist, plus the single sliding pill.
      renderTabs() {
        this.list.innerHTML = '';
        this.tabButtons = this.groups.map((g, i) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ndog-tabs__tab';
          btn.setAttribute('role', 'tab');
          btn.setAttribute('aria-selected', 'false');
          btn.setAttribute('tabindex', '-1');
          btn.dataset.index = String(i);
          // Styling hook: lets CSS target one specific tab by its key (e.g. a
          // non-flavor tab that should look different) without relying on the
          // position, which shifts as soon as tabs are reordered or added.
          btn.dataset.key = g.key;
          // Label lives in its own span so it stays above the pill while it slides.
          const label = document.createElement('span');
          label.className = 'ndog-tabs__tab-label';
          label.textContent = g.label || this.humanize(g.key) || `Tab ${i + 1}`;
          btn.appendChild(label);
          this.list.appendChild(btn);
          return btn;
        });

        // The moving highlight — one per tablist, positioned/sized by movePill().
        this.pill = document.createElement('span');
        this.pill.className = 'ndog-tabs__pill';
        this.pill.setAttribute('aria-hidden', 'true');
        this.list.appendChild(this.pill);

        // NOTE: the kept reference survives the innerHTML reset above, so the CTA
        // is re-attached on every rebuild (incl. theme-editor section reloads).
        if (this.ctaLink) this.list.appendChild(this.ctaLink);

        this.markFirstRow();
      }

      // Tag the tab buttons sitting on the first wrapped row — CSS stretches only
      // that row on mobile. Stable: flex-grow never changes line wrapping, so the
      // row assignment can't oscillate. Re-run on resize and font load.
      markFirstRow() {
        if (!this.tabButtons || this.tabButtons.length === 0) return;
        const firstTop = this.tabButtons[0].offsetTop;
        this.tabButtons.forEach((btn) => {
          btn.classList.toggle('ndog-tabs__tab--first-row', btn.offsetTop === firstTop);
        });
      }

      bindEvents() {
        // Activate on button click.
        this.onClick = (e) => {
          const btn = e.target.closest('.ndog-tabs__tab');
          if (!btn) return;
          const index = Number(btn.dataset.index);
          this.activate(index, { animate: true, updateHash: true, userInitiated: true });
          this.tabButtons[index].focus();
        };
        this.list.addEventListener('click', this.onClick);

        // Keyboard navigation within the tablist (manual activation).
        this.onKeydown = (e) => this.handleKeydown(e);
        this.list.addEventListener('keydown', this.onKeydown);

        // Deep-link: react to hash changes (e.g. anchor links on the page).
        this.onHashChange = () => {
          const idx = this.indexForHash();
          if (idx >= 0) this.activate(idx, { animate: true });
        };
        window.addEventListener('hashchange', this.onHashChange);

        // Keep the pill aligned when the layout reflows (rAF-throttled, no slide).
        let resizeTicking = false;
        this.onResize = () => {
          if (resizeTicking) return;
          resizeTicking = true;
          requestAnimationFrame(() => {
            this.markFirstRow();
            this.movePill(false);
            resizeTicking = false;
          });
        };
        window.addEventListener('resize', this.onResize, { passive: true });

        this.bindSwipe();
        this.bindEditor();
      }

      // Move focus with arrows/Home/End; activate with Enter/Space.
      handleKeydown(e) {
        const tabs = this.tabButtons;
        const focused = tabs.indexOf(document.activeElement);
        let next = null;

        switch (e.key) {
          case 'ArrowRight':
          case 'ArrowDown':
            next = (focused + 1) % tabs.length;
            break;
          case 'ArrowLeft':
          case 'ArrowUp':
            next = (focused - 1 + tabs.length) % tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = tabs.length - 1;
            break;
          case 'Enter':
          case ' ':
            if (focused >= 0) {
              e.preventDefault();
              this.activate(focused, { animate: true, updateHash: true, userInitiated: true });
            }
            return;
          default:
            return;
        }

        e.preventDefault();
        tabs[next].focus();
      }

      // Horizontal swipe on the active group switches to the neighbouring tab.
      bindSwipe() {
        let startX = 0;
        let startY = 0;
        let tracking = false;

        this.onTouchStart = (e) => {
          const t = e.touches[0];
          startX = t.clientX;
          startY = t.clientY;
          // Only track swipes that begin inside the currently visible group,
          // but never inside a horizontal slider (it owns its own swipe).
          const active = this.groups[this.activeIndex];
          tracking =
            !!active &&
            active.sections.some((s) => s.contains(e.target)) &&
            !e.target.closest('[data-no-tab-swipe]');
        };
        this.onTouchEnd = (e) => {
          if (!tracking) return;
          tracking = false;
          const t = e.changedTouches[0];
          const dx = t.clientX - startX;
          const dy = t.clientY - startY;
          // Require a mostly-horizontal move past the threshold so vertical scroll is untouched.
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
          const dir = dx < 0 ? 1 : -1;
          const target = this.activeIndex + dir;
          if (target >= 0 && target < this.groups.length) {
            this.activate(target, { animate: true, updateHash: true, direction: dir, userInitiated: true });
          }
        };
        this.container.addEventListener('touchstart', this.onTouchStart, { passive: true });
        this.container.addEventListener('touchend', this.onTouchEnd, { passive: true });
      }

      // Theme editor: follow section selection and rebuild on add/remove.
      bindEditor() {
        if (!window.Shopify || !window.Shopify.designMode) return;

        this.onSelect = (e) => {
          const idx = this.groups.findIndex((g) => g.sections.some((s) => s.contains(e.target)));
          if (idx >= 0) this.activate(idx, { animate: false });
        };
        document.addEventListener('shopify:section:select', this.onSelect);

        this.onSectionChange = () => {
          const previousKey = this.activeIndex != null ? this.groups[this.activeIndex].key : null;
          this.resetSections();
          this.buildGroups();
          if (this.groups.length === 0) {
            this.list.innerHTML = '';
            return;
          }
          this.renderTabs();
          const idx = this.groups.findIndex((g) => g.key === previousKey);
          this.activate(idx >= 0 ? idx : 0, { animate: false });
        };
        document.addEventListener('shopify:section:load', this.onSectionChange);
        document.addEventListener('shopify:section:unload', this.onSectionChange);
      }

      // Show the chosen group, hide the rest, update buttons and (optionally) the hash.
      activate(index, opts = {}) {
        if (!this.groups.length) return;
        index = Math.max(0, Math.min(index, this.groups.length - 1));
        const direction = opts.direction != null
          ? opts.direction
          : this.activeIndex == null || index >= this.activeIndex
            ? 1
            : -1;
        const animate = opts.animate && !this.reduceMotion;

        this.groups.forEach((g, i) => {
          const show = i === index;
          g.sections.forEach((sec) => {
            sec.classList.remove('ndog-tab-anim-in');
            sec.classList.toggle('ndog-tab-hidden', !show);
            if (show && animate) this.playEntry(sec, direction);
          });
        });

        this.tabButtons.forEach((btn, i) => {
          const selected = i === index;
          btn.setAttribute('aria-selected', selected ? 'true' : 'false');
          btn.setAttribute('tabindex', selected ? '0' : '-1');
        });

        this.activeIndex = index;
        this.movePill(animate);

        if (opts.updateHash) {
          history.replaceState(null, '', `#${this.groups[index].key}`);
        }

        // Sticky mode: jump back to the container top on a real tab switch, then
        // re-evaluate the release offset for the new group.
        if (this.sticky) {
          if (opts.userInitiated) this.scrollToContainerTop();
          this.updateRelease();
        }
      }

      // Slide the highlight pill onto the active button. animate=false suppresses
      // the CSS transition (initial placement, resize, theme-editor jumps).
      movePill(animate) {
        const btn = this.tabButtons[this.activeIndex];
        if (!btn || !this.pill) return;
        const apply = () => {
          this.pill.style.transform = `translate(${btn.offsetLeft}px, ${btn.offsetTop}px)`;
          this.pill.style.width = `${btn.offsetWidth}px`;
          this.pill.style.height = `${btn.offsetHeight}px`;
        };
        if (animate) {
          apply();
        } else {
          this.pill.style.transition = 'none';
          apply();
          void this.pill.offsetWidth; // reflow so the next move animates again
          this.pill.style.transition = '';
        }
        this.pill.classList.add('ndog-tabs__pill--ready');
      }

      // Run the directional entry animation on one section, then clean up.
      playEntry(sec, direction) {
        sec.style.setProperty('--ndog-tab-dir', String(direction));
        void sec.offsetWidth; // force reflow so the animation restarts
        sec.classList.add('ndog-tab-anim-in');
        sec.addEventListener(
          'animationend',
          () => {
            sec.classList.remove('ndog-tab-anim-in');
            sec.style.removeProperty('--ndog-tab-dir');
          },
          { once: true }
        );
      }

      // Sticky: once the active group's bottom passes the sticky line, let the bar
      // ride up with the content (translateY) instead of staying pinned all the way down.
      setupStickyRelease() {
        if (!this.sticky) return;
        let ticking = false;
        this.onStickyScroll = () => {
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            this.updateRelease();
            ticking = false;
          });
        };
        window.addEventListener('scroll', this.onStickyScroll, { passive: true });
        window.addEventListener('resize', this.onStickyScroll, { passive: true });
        this.updateRelease();
      }

      // Translate the bar up by however far the active group's bottom sits above the
      // sticky line; stays at 0 (pinned) while there is still content to scroll through.
      updateRelease() {
        if (!this.sticky || this.activeIndex == null || !this.sectionWrapper) return;
        const last = this.groups[this.activeIndex].sections.at(-1);
        if (!last) {
          this.sectionWrapper.style.transform = '';
          return;
        }
        const limit = this.getStickyOffset() + this.sectionWrapper.offsetHeight;
        const delta = Math.min(0, last.getBoundingClientRect().bottom - limit);
        this.sectionWrapper.style.transform = delta < 0 ? `translateY(${delta}px)` : '';
      }

      // Scroll up so the active group starts just below the pinned bar — only when the
      // bar is currently stuck (i.e. the user has scrolled into the tab area).
      scrollToContainerTop() {
        if (!this.sticky || !this.sectionWrapper) return;
        const offset = this.getStickyOffset();
        if (this.sectionWrapper.getBoundingClientRect().top > offset + 1) return;
        const first = this.groups[this.activeIndex].sections[0];
        if (!first) return;
        const top =
          window.scrollY + first.getBoundingClientRect().top - offset - this.sectionWrapper.offsetHeight;
        window.scrollTo({ top, behavior: this.reduceMotion ? 'auto' : 'smooth' });
      }

      // Current sticky top offset in px, read live from the section wrapper's resolved
      // `top` so it follows the active mobile/desktop breakpoint. 0 when not sticky.
      getStickyOffset() {
        if (!this.sticky || !this.sectionWrapper) return 0;
        return parseFloat(getComputedStyle(this.sectionWrapper).top) || 0;
      }

      // Strip every class/var this component may have set on sibling sections.
      resetSections() {
        if (!this.groups) return;
        this.groups.forEach((g) => {
          g.sections.forEach((sec) => {
            sec.classList.remove('ndog-tab-hidden', 'ndog-tab-anim-in');
            sec.style.removeProperty('--ndog-tab-dir');
          });
        });
      }

      // Resolve the matching group index for the current URL hash (-1 if none).
      indexForHash() {
        const hash = decodeURIComponent(window.location.hash.replace('#', '')).trim();
        if (!hash) return -1;
        return this.groups.findIndex((g) => g.key === hash);
      }

      // Initial tab: a valid hash wins, otherwise the configured default.
      getInitialIndex() {
        const fromHash = this.indexForHash();
        if (fromHash >= 0) return fromHash;
        return Number(this.dataset.defaultTab || 0) || 0;
      }
    }
  );
}
